import React, { memo, useMemo, useState } from 'react';
import CoworkerDetails from 'components/CoworkerDetails/CoworkerDetails';
import { CoworkerGridData, DummyGridData } from 'types/appContext';
import useApp from 'hooks/useApp';
import { BEGINNING_OF_TIME, INDEFINITE_END_DATE } from 'utils/date';
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import Tooltip from '@ingka/tooltip';
import Button from '@ingka/button';
import { COWORKER_CHANGE_CONTRACT_BUTTON__CLICK, COWORKER_RESET_BUTTON__CLICK } from 'types/analytics';
import reset from '@ingka/ssr-icon/paths/arrow-u-turn-backwards';
import { useTranslation } from 'react-i18next';
import pencil from '@ingka/ssr-icon/paths/pencil';
import { useDeleteScenarioCoworkerAPI } from 'hooks/api/useDeleteScenarioCoworkerAPI';
import ModalService from 'services/modal';
import { useToast } from 'hooks/useToast';
import { createPortal } from 'react-dom';
import useData from 'hooks/useData';
import { NoScenario, type Scenario } from 'types/scenario';
import useAuth from 'hooks/useAuth';
import { UserPermission } from 'types/authContext';
import { rowIsContractHours } from 'hooks/useGrid/gridFunctionsShared';
import { EmploymentStatus } from 'types/coworker';
import Text from '@ingka/text';
import { ContractHoursRow } from './ContractHoursRow';
import { getCoworkerDelta } from './shared';
import classes from '../CoworkerDetails/CoworkerDetails.module.scss';

const isPrimaryRow = (index: number) => index === 0;

const CoworkerRows = memo(
    ({
        coworkerData,
        canEdit,
        showAllDetails,
    }
    : {
        coworkerData: CoworkerGridData | DummyGridData;
        canEdit: boolean,
        showAllDetails: boolean,
    }) => {
        const { t } = useTranslation();
        const { coworker, gridRows } = coworkerData;
        const { currentCoworkerId } = useApp();
        const [showIndividualDetails, setShowIndividualDetails] = useState(!!currentCoworkerId || false);
        const { currentScenario, selectScenario } = useData();
        const [deleteCoworkerModal, setDeleteCoworkerModal] = useState<boolean>(false);
        const [openEditCoworkerModal, setOpenEditCoworkerModal] = useState<boolean>(false);
        const { deleteCoworker } = useDeleteScenarioCoworkerAPI(coworkerData.coworker.personId);
        const { displayToast } = useToast();
        const { user } = useAuth();
        const hasEditScenarioAccess = user?.rolePermissions.includes(UserPermission.EDIT_SCENARIO) ?? false;

        const handleClickDetails = () => {
            setShowIndividualDetails(show => !show);
        };

        // TODO: Use Contract Dates instead validity dates when comparing latest contract dates. Check with BE
        const { newContractStartDate, newContractEndDate } = useMemo(() => getCoworkerDelta(
            currentScenario?.scenarioData?.find(el => el.personId === coworker.personId)?.coworkerDelta,
            currentScenario?.scenarioData?.find(el => el.personId === coworker.personId)?.contractDeltas,
            coworker.contractStartDate ?? BEGINNING_OF_TIME,
            coworker.contractEndDate ?? INDEFINITE_END_DATE,
        ), [coworker, currentScenario]);

        // TODO: Move to Backend getContractDatesColSpan
        const isActive = coworker.status === EmploymentStatus.ACTIVE || !coworker.status;
        const isResetButtonEnabled = currentScenario?.scenarioData.some(({ personId }) => personId === coworkerData.coworker.personId);
        const getResetButtonText = (scenario: Scenario | undefined) => {
            if (!scenario) {
                return t('SELECT_A_SCENARIO');
            }

            return isResetButtonEnabled ? t('RESET_CHANGES') : t('NO_CHANGES_CO_WORKER');
        };

        const portalDiv = document.getElementById('portal');
        const confirmDeleteModal = ModalService().open(
            'prompt',
            deleteCoworkerModal,
            setDeleteCoworkerModal,
            <div>
                {coworkerData.coworker.isDummy
                    ? <Text tagName="p" textStyle="Body.M">{t('DELETE_CO_WORKER_PROMPT')}</Text>
                    : <Text tagName="p" textStyle="Body.M">{t('RESET_CO_WORKER_PROMPT')}</Text>}
            </div>,
            <div className={classes['delete-prompt-btn']}>
                <Button
                    text={coworkerData.coworker.isDummy ? t('DELETE_CO_WORKER_CONFIRM') : t('RESET_CO_WORKER_CONFIRM')}
                    size="small"
                    type="danger"
                    data-testid="confirm-delete-coworker-button"
                    onClick={() => {
                        setDeleteCoworkerModal(false);
                        deleteCoworker().then(response => {
                            if (!response.isResponseOk) {
                                displayToast({ title: t('ERROR'), message: t('OPERATION_FAILED') });
                            } else {
                                displayToast({ title: t('SUCCESS'), message: t('SCENARIO_UPDATED') });
                                selectScenario(currentScenario?.id ?? NoScenario);
                            }
                        });
                    }}
                    autoFocus
                />
                <Button
                    text={t('CANCEL')}
                    size="small"
                    type="secondary"
                    onClick={() => setDeleteCoworkerModal(false)}
                />
            </div>,
            coworkerData.coworker.isDummy ? t('DELETE_CO_WORKER') : t('RESET_CHANGES'),
        );
        const coworkerDetailsModal = (
            <Modal
                visible={showIndividualDetails}
                escapable
                handleCloseBtn={() => setShowIndividualDetails(false)}
                focusLockProps={{ locked: false }}
            >
                <Sheets
                    alignment="right"
                    size="small"
                    header={(<ModalHeader />)}
                    footer={(
                        <ModalFooter>
                            {hasEditScenarioAccess && (!currentScenario ? null
                                : (
                                    <>
                                        <Tooltip
                                            describedById="coworker-reset-button-tooltip"
                                            tooltipText={getResetButtonText(currentScenario)}
                                            position="trailing"
                                        >
                                            <Button
                                                data-testid="coworker-reset-button"
                                                ssrIcon={reset}
                                                onClick={() => setDeleteCoworkerModal(true)}
                                                size="small"
                                                disabled={!isResetButtonEnabled}
                                                type="secondary"
                                                text={coworkerData.coworker.isDummy ? t('DELETE_CO_WORKER') : t('RESET_CHANGES')}
                                                data-analytics={COWORKER_RESET_BUTTON__CLICK}
                                            />
                                        </Tooltip>
                                        <Tooltip
                                            describedById="coworker-edit-button-tooltip"
                                            tooltipText={t('CHANGE_SCENARIO_DATA')}
                                            position="top"
                                        >
                                            <Button
                                                data-testid="coworker-edit-button"
                                                ssrIcon={pencil}
                                                onClick={() => setOpenEditCoworkerModal(true)}
                                                size="small"
                                                disabled={!currentScenario}
                                                type="primary"
                                                text={t('CHANGE_SCENARIO_DATA')}
                                                data-analytics={COWORKER_CHANGE_CONTRACT_BUTTON__CLICK}
                                                autoFocus
                                            />
                                        </Tooltip>
                                    </>
                                ))}
                        </ModalFooter>
                    )}
                >
                    <ModalBody>
                        <CoworkerDetails
                            coworker={coworkerData.coworker}
                            isActive={isActive}
                            editCoworkerSheet={openEditCoworkerModal}
                            handleEditCoworkerSheet={setOpenEditCoworkerModal}
                        />
                    </ModalBody>
                </Sheets>
            </Modal>
        );

        return (
            <>
                {gridRows.map((gridRow, index) => (
                    <React.Fragment key={`${coworker.employeeId}-${gridRow.rowType}-${gridRow.costCentre}`}>
                        {rowIsContractHours(gridRow)
                                && (
                                    <ContractHoursRow
                                        key={`${coworker.personId}-${globalThis.crypto.randomUUID()}-contract-row`}
                                        coworker={coworker}
                                        showAllDetails={showAllDetails}
                                        cellProps={gridRow.editableCells}
                                        canEdit={canEdit}
                                        averageHrs={gridRow.rowAverage}
                                        totalHours={{ contractHours: gridRow.rowSum }}
                                        costCentre={gridRow.costCentre}
                                        handleClickDetails={handleClickDetails}
                                        isPrimaryRow={isPrimaryRow(index)}
                                        isActive={isActive}
                                        contractStartDate={newContractStartDate}
                                        contractEndDate={newContractEndDate ?? INDEFINITE_END_DATE}
                                    />
                                )}

                    </React.Fragment>
                ))}
                {portalDiv && deleteCoworkerModal && createPortal(confirmDeleteModal, portalDiv)}
                {portalDiv && createPortal(coworkerDetailsModal, portalDiv)}
            </>
        );
    },
);

export default CoworkerRows;
